// src/shared/noCompany.js — „Записи без фирма" (само админ).
//
// Решение на Макс (16.10.2026): служителите и клиентите вече не виждат
// заварените записи без фирма (companyGuard в auth.js). Админът ги вижда тук
// и ги прикачва към фирма. Входът е лента най-горе на таблото — стои само
// докато има такива записи, за да не се търси в менютата.

import { el, clear } from './dom.js';
import { api } from './api.js';
import { toast } from './toast.js';
import { openModal, closeModal } from './modal.js';
import { state, isAdminUser } from './state.js';

const countText = (n) => (n === 1 ? '1 стар запис няма фирма' : n + ' стари записа нямат фирма');

// Лентата на таблото. Мълчи, ако няма такива записи или заявката не мине —
// таблото не бива да страда заради нея.
export async function noCompanyBanner(page, onDone) {
  if (!isAdminUser()) return null;
  let total = 0;
  try { total = Number((await api('/no-company/count')).total) || 0; } catch (e) { return null; }
  if (!total || !page.isConnected || page.querySelector('.nc-banner')) return null;
  const bar = el('div', { class: 'sev-warn nc-banner', role: 'status' },
    el('span', { text: '⚠ ' + countText(total) + ' — служителите и клиентите не ги виждат, докато не ги прикачиш към фирма.' }),
    el('button', { class: 'btn sm', type: 'button', text: 'Прикачи към фирма', onClick: () => openNoCompanyDialog(onDone) }));
  page.insertBefore(bar, page.firstChild);
  return bar;
}

export async function openNoCompanyDialog(onDone) {
  const body = el('div', { class: 'nc-body' }, el('p', { class: 'muted', text: 'Зарежда…' }));
  const foot = el('div', {});
  let picks = [];
  let overlay = null;

  async function load(failed) {
    clear(body);
    clear(foot);
    picks = [];
    let data;
    try {
      data = await api('/no-company');
    } catch (e) {
      body.appendChild(el('p', { text: e.message }));
      foot.appendChild(el('button', { class: 'btn', type: 'button', text: 'Затвори', onClick: closeModal }));
      return;
    }
    if (failed && failed.length) {
      body.appendChild(el('div', { class: 'sev-warn', role: 'alert' },
        el('strong', { text: 'Не се прикачиха:' }),
        el('ul', {}, ...failed.map((f) => el('li', { text: (f.text ? f.text + ' — ' : '') + f.error })))));
    }
    if (!data.total) {
      body.appendChild(el('p', { text: 'Няма записи без фирма — всичко е прикачено.' }));
      foot.appendChild(el('button', { class: 'btn', type: 'button', text: 'Затвори', onClick: closeModal }));
      return;
    }
    body.appendChild(el('p', { class: 'muted', text: 'Тези записи са от едно време, без фирма. Служителите и клиентите вече не ги виждат — само ти. Избери фирма до всеки и натисни „Прикачи". Където има подсказка, тя идва от клиента или сделката на записа.' }));
    const companies = state().companies || [];
    for (const sec of data.sections) {
      body.appendChild(el('h4', { text: sec.label + ' (' + sec.rows.length + ')' }));
      if (!sec.assign) {
        body.appendChild(el('p', { class: 'muted', text: 'Тези не се прикачват оттук — номерът или наличностите им зависят от фирмата. Ако ти трябват, кажи на Claude.' }));
      }
      const tbody = el('tbody');
      for (const r of sec.rows) {
        let cell = el('td', { text: '—' });
        if (sec.assign) {
          const sel = el('select', { 'aria-label': 'Фирма за ' + sec.label.toLowerCase() + ': ' + r.text },
            el('option', { value: '', text: '— избери —' }),
            ...companies.map((c) => el('option', { value: String(c.id), text: c.name })));
          if (r.suggest) sel.value = String(r.suggest.company_id);
          picks.push({ sel, collection: sec.collection, id: r.id });
          cell = el('td', {}, sel, r.suggest ? el('div', { class: 'muted nc-why', text: r.suggest.why }) : null);
        }
        tbody.appendChild(el('tr', {}, el('td', { text: r.text }), el('td', { text: r.date || '' }), cell));
      }
      body.appendChild(el('table', { class: 'list nc-table' },
        el('thead', {}, el('tr', {}, el('th', { text: 'Запис' }), el('th', { text: 'Дата' }), el('th', { text: 'Фирма' }))),
        tbody));
    }
    foot.appendChild(el('button', { class: 'btn ghost', type: 'button', text: 'Затвори', onClick: closeModal }));
    if (picks.length) foot.appendChild(el('button', { class: 'btn', type: 'button', text: 'Прикачи', onClick: save }));
  }

  async function save() {
    const items = picks.filter((p) => p.sel.value)
      .map((p) => ({ collection: p.collection, id: p.id, company_id: Number(p.sel.value) }));
    if (!items.length) { toast('Избери фирма поне до един запис.', true); return; }
    try {
      const r = await api('/no-company/assign', { method: 'POST', body: JSON.stringify({ items }) });
      if (r.assigned.length) toast('Прикачени: ' + r.assigned.length + '.');
      // Прозорецът остава отворен с каквото е останало и с причините за
      // неприкачените — така нищо не изчезва безследно.
      await load(r.failed);
      // Прикаченото е записано — изходът с Esc/„×" няма за какво да пита.
      if (overlay) overlay._dirty = false;
      if (onDone) onDone();
    } catch (e) { toast(e.message, true); }
  }

  overlay = openModal({ title: 'Записи без фирма', body, foot, wide: true });
  await load();
}
